import React, { useEffect, useMemo, useState } from "react";
import { Layers, Clock, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";

const TABS = [
  ["d_vah", "Above Daily Node-H", "text-emerald-600", "D Node-H"], ["w_vah", "Above Weekly Node-H", "text-emerald-600", "W Node-H"],
  ["d_val", "Below Daily Node-L", "text-rose-600", "D Node-L"], ["w_val", "Below Weekly Node-L", "text-rose-600", "W Node-L"],
];
const ZONE = { D_narrow: "NZ D", D_inside: "NZ IN D", W_narrow: "NZ W", W_inside: "NZ IN W" };
const chip = (on) => `h-6 px-2 text-[10px] font-mono border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-500 hover:border-blue-500"}`;
const tag = "ml-1 text-[9px] px-1 font-bold";

export const copyTv = async (rows) => {
  if (!rows.length) return toast.error("No stocks to copy");
  const text = rows.map((r) => `NSE:${r.symbol.replace(/[&-]/g, "_")}`).join(",");
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${rows.length} symbols — paste into a TradingView watchlist`);
  } catch {
    toast.error("Copy failed — clipboard blocked");
  }
};

export const Marks = ({ r, hot }) => (
  <>
    {r.index && <span className={`${tag} bg-blue-600 text-white`} data-testid={`va-index-${r.symbol}`}>INDEX</span>}
    {hot?.top?.includes(r.sector) && <span className={`${tag} bg-emerald-600 text-white`} data-testid={`va-top-${r.symbol}`}>TOP</span>}
    {hot?.bottom?.includes(r.sector) && <span className={`${tag} bg-rose-600 text-white`} data-testid={`va-worst-${r.symbol}`}>WORST</span>}
    {r.big > 0 && <span className={`${tag} ${r.big === 3 ? "bg-amber-400 text-black" : "bg-amber-500/20 text-amber-300 border border-amber-500/50"}`}
      title={`Breaking candle volume ${r.vol_x}× usual for that time`} data-testid={`va-big-${r.symbol}`}>BIG {r.big}×</span>}
    {r.oi && <span className={`${tag} bg-cyan-500/15 text-cyan-300 border border-cyan-500/50`} title={`#${r.oi.rank} OI gainer · OI +${r.oi.oi_chg}% · ${r.oi.buildup}`} data-testid={`va-oi-${r.symbol}`}>OI #{r.oi.rank}</span>}
    {(r.zone || []).map((z) => <span key={z} className={`${tag} bg-violet-500/15 text-violet-300 border border-violet-500/50`} data-testid={`va-zone-${z}-${r.symbol}`}>{ZONE[z]}</span>)}
  </>
);

const Row = ({ r, lbl, hot }) => (
  <div className="flex items-center gap-1 border-b border-slate-200">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
      <button className={`flex-1 min-w-0 flex items-center gap-3 text-xs font-mono px-2 py-1.5 hover:bg-blue-500/10 transition-colors text-left ${r.big ? "border-l-2 border-amber-400" : r.oi ? "border-l-2 border-cyan-400" : r.zone?.length ? "border-l-2 border-violet-400" : ""}`} data-testid={`va-row-${r.symbol}`}>
        <span className="flex-1 min-w-0 text-slate-900 font-semibold">{r.symbol}<Marks r={r} hot={hot} />
          <span className="block text-[9px] text-slate-500 font-normal">{r.sector} · 1st {r.first_at}{r.crosses > 1 ? ` · ${r.crosses}×` : ""}</span></span>
        <span className="w-24 text-right text-slate-700">{inr(r.ltp)}</span>
        <span className="w-28 text-right text-slate-500 text-[10px]">{lbl} {inr(r.level)}</span>
        <span className={`w-16 text-right ${r.dist_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.dist_pct > 0 ? "+" : ""}{r.dist_pct}%</span>
        <span className={`w-16 text-right ${r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.change_pct > 0 ? "+" : ""}{r.change_pct}%</span>
        <span className="w-16 text-[10px] px-1.5 py-px border border-blue-500/40 bg-blue-500/10 text-sky-300 flex items-center gap-1" data-testid={`va-time-${r.symbol}`}><Clock className="h-2.5 w-2.5" />{r.cross_at}</span>
      </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} />
  </div>
);

export default function VaCross() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("d_vah");
  const [sec, setSec] = useState(null);
  const [marked, setMarked] = useState(false);
  useEffect(() => {
    const load = () => api.get("/today/va-cross").then((r) => setD(r.data)).catch(() => setD({ lists: {} }));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const keep = (r) => (!marked || r.big > 0 || r.oi || r.zone?.length) && (!sec || r.sector === sec);
  const L = d?.lists || {};
  const sectors = useMemo(() => {
    const c = {};
    (L[tab] || []).forEach((r) => { c[r.sector] = (c[r.sector] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [L, tab]);
  const rows = (L[tab] || []).filter(keep);
  const cur = TABS.find(([k]) => k === tab);
  return (
    <div className="space-y-3" data-testid="va-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Layers className="h-5 w-5 text-blue-600" /> TFR Value Node Breaks</h1>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">fresh 5 min close beyond yesterday's / last week's 70% TFR Value Node (Node-H / Node-L) · F&O stocks + NIFTY, BANKNIFTY (futures) · session {d?.session || "—"}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-1" data-testid="va-tabs">
        {TABS.map(([k, label, c]) => (
          <button key={k} onClick={() => { setTab(k); setSec(null); }} data-testid={`va-tab-${k}`}
            className={`h-9 text-xs font-mono font-bold border transition-colors ${tab === k ? `border-blue-500 ${c} bg-blue-500/15` : "border-slate-300 text-slate-500 hover:text-slate-900"}`}>
            {label} <span className="text-slate-500" data-testid={`va-count-${k}`}>{(L[k] || []).filter(keep).length}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <button onClick={() => setMarked(!marked)} aria-pressed={marked} data-testid="va-marked-toggle" className={chip(marked)}>Only BIG / OI / NZ marked</button>
        <span className="w-2" />
        <button onClick={() => setSec(null)} className={chip(!sec)} data-testid="va-sector-all">All</button>
        {sectors.map(([s, n]) => (
          <button key={s} onClick={() => setSec(sec === s ? null : s)} className={chip(sec === s)} data-testid={`va-sector-${s.replace(/\W+/g, "-")}`}>{s} <span className="opacity-60">{n}</span></button>
        ))}
        <button onClick={() => copyTv(rows)} data-testid="va-copy-tv"
          className="ml-auto h-7 px-3 text-xs font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5"><Copy className="h-3.5 w-3.5 text-blue-600" /> Copy to TradingView</button>
      </div>
      <div className="border border-slate-200 bg-white" data-testid={`va-list-${tab}`}>
        <div className="flex gap-3 px-2 py-1 text-[9px] uppercase text-slate-500 font-mono border-b border-slate-200">
          <span className="flex-1">Stock · marks</span><span className="w-24 text-right">Price</span><span className="w-28 text-right">Level</span>
          <span className="w-16 text-right">From lvl</span><span className="w-16 text-right">Day %</span><span className="w-16">Latest</span><span className="w-7" />
        </div>
        {!d ? <p className="p-4 text-xs font-mono text-slate-500">Loading…</p>
          : rows.length ? rows.map((r) => <Row key={r.symbol} r={r} lbl={cur[3]} hot={d.hot} />)
            : <p className="p-4 text-xs font-mono text-slate-500" data-testid="va-empty">{marked || sec ? "No breaks match these filters" : "No fresh breaks yet today"}</p>}
      </div>
      <p className="text-[10px] font-mono text-slate-500">BIG = breaking candle ≥2× usual volume for that time · OI # = top 20 OI gainer · NZ = narrow Flow Zone (IN = inside) · TOP / WORST = best / worst 2 sectors · 1st = first break, clock = latest</p>
    </div>
  );
}
